import {
    getInternetPricing,
    internetEppEligible,
    internetMobilityDiscountEligible
} from './internetPricing';
import { getTvPriceBreakdown } from './tvPricing';
import { nullableNumberOrFallback } from './pricingNumbers';
import { formatMoney } from './formatters';

const HOME_SERVICES_ONLY_WARNING = 'Only Internet, TV, and Home Phone are included in this approved Home Services email preview. Other selected items are quote-only for now.';

export function buildCustomerEmailModel({
    builder,
    mobilityDiscountActive,
    selectedAddons = [],
    selectedInternet,
    selectedLongDistance = [],
    selectedMobility,
    selectedPhone,
    selectedSecurity,
    selectedTv,
    activeTvPriceAdjustment,
    activeTvGiftAdjustment,
    activeEmailShell,
    billCreditAmount = 50
}) {
    const rows = [];
    const internalWarnings = [];
    let homeSubtotal = 0;
    let regularTotal = 0;

    const addRow = ({ product, promo, regular, regularLabel, bullets = [] }) => {
        rows.push({ product, promo, regular, regularLabel, bullets });
        homeSubtotal += promo;
        regularTotal += typeof regular === 'number' ? regular : promo;
    };

    if (selectedInternet && selectedInternet.customerEmailEligible !== false) {
        const internetPricing = getInternetPricing(
            selectedInternet,
            mobilityDiscountActive,
            builder.eppEnabled,
            builder.pabIncluded
        );
        const internetEmailRegularPrice = nullableNumberOrFallback(
            selectedInternet.emailRegularPrice,
            internetPricing.belowLine
        );

        addRow({
            product: selectedInternet.emailDisplayName || `PureFibre Internet ${selectedInternet.name}`,
            promo: internetPricing.belowLine,
            regular: internetEmailRegularPrice,
            regularLabel: `${formatMoney(internetEmailRegularPrice)}/mo`,
            bullets: selectedInternet.emailBullets || ['Unlimited Data Usage']
        });
    } else if (selectedInternet) {
        internalWarnings.push('Selected Internet plan is not available for customer email yet.');
    }

    if (selectedTv && selectedTv.customerEmailEligible !== false) {
        const tvPriceBreakdown = getTvPriceBreakdown(
            selectedTv,
            builder.eppEnabled,
            activeTvPriceAdjustment,
            activeTvGiftAdjustment
        );
        const tvRegularPrice = Number(selectedTv.regularPrice ?? selectedTv.price ?? 0)
            + Number(tvPriceBreakdown.adjustmentAmount || 0);

        addRow({
            product: selectedTv.emailDisplayName || selectedTv.name,
            promo: tvPriceBreakdown.finalTotal,
            regular: tvRegularPrice,
            regularLabel: `${formatMoney(tvRegularPrice)}/mo`,
            bullets: selectedTv.emailBullets || ['Included: TELUS TV Digital Box']
        });

        if ((selectedTv.pvrMode === 'optional' && builder.addPvr) || builder.extraBoxes > 0) {
            internalWarnings.push(HOME_SERVICES_ONLY_WARNING);
        }
    } else if (selectedTv) {
        internalWarnings.push('Selected TV package is not available for customer email yet.');
    }

    if (selectedPhone && selectedPhone.customerEmailEligible !== false) {
        addRow({
            product: selectedPhone.emailDisplayName || selectedPhone.name,
            promo: selectedPhone.price,
            regular: selectedPhone.originalPrice,
            regularLabel: `${formatMoney(selectedPhone.originalPrice)}/mo`,
            bullets: selectedPhone.emailBullets || [selectedPhone.features]
        });
    } else if (selectedPhone) {
        internalWarnings.push('Selected Home Phone plan is not available for customer email yet.');
    }

    const hasHomeServices = Boolean(selectedInternet || selectedTv || selectedPhone);
    const billCredit = selectedInternet
        && internetMobilityDiscountEligible(selectedInternet)
        && mobilityDiscountActive
        && (selectedTv || selectedPhone)
        ? billCreditAmount
        : 0;
    const currentMonthlyBill = Number(builder.currentMonthlyBill);
    const hasCurrentMonthlyBill = Number.isFinite(currentMonthlyBill) && currentMonthlyBill > 0;
    const totalSavings = regularTotal > homeSubtotal ? (regularTotal - homeSubtotal) * 24 : null;
    const activeCustomerDiscounts = [
        selectedInternet && builder.pabIncluded ? 'PAB' : null,
        selectedInternet && internetMobilityDiscountEligible(selectedInternet) && mobilityDiscountActive ? 'M&H' : null,
        builder.eppEnabled && ((selectedInternet && internetEppEligible(selectedInternet)) || selectedTv) ? 'EPP' : null
    ].filter(Boolean);
    const discountFootnote = activeCustomerDiscounts.length === 1 && activeCustomerDiscounts[0] === 'PAB'
        ? '*Price shown includes PAB pre-authorized bank payment discount. Customer must set up auto-pay after installation. Taxes extra.'
        : activeCustomerDiscounts.length > 0
            ? '*Prices shown include selected discounts where applicable. PAB requires pre-authorized bank payment setup after installation. TELUS/Koodo mobility and EPP discounts are subject to eligibility. Taxes extra.'
            : null;

    if (
        selectedLongDistance.length > 0
        || selectedAddons.length > 0
        || selectedSecurity
        || builder.mobilityState === 'adding'
    ) {
        internalWarnings.push(HOME_SERVICES_ONLY_WARNING);
    }
    if (builder.mobilityState === 'adding' && selectedMobility) {
        internalWarnings.push(HOME_SERVICES_ONLY_WARNING);
    }

    const customerIncludedFeatures = [
        selectedInternet
            ? { label: '1 FREE wireless modem rental', icon: activeEmailShell.wifiIcon, html: '<strong>1 FREE</strong> wireless modem rental' }
            : null,
        selectedTv
            ? { label: '1 FREE digital box rental', icon: activeEmailShell.boxIcon, html: '<strong>1 FREE</strong> digital box rental' }
            : null,
        selectedTv && (selectedTv.pvrIncluded || (selectedTv.pvrMode === 'optional' && builder.addPvr))
            ? { label: '1 FREE PVR rental', icon: activeEmailShell.pvrIcon || activeEmailShell.boxIcon, html: '<strong>1 FREE</strong> PVR rental' }
            : null,
        hasHomeServices
            ? { label: 'Waive Professional Install Fee', icon: activeEmailShell.installIcon, html: 'Waive Professional Install Fee' }
            : null,
        billCredit > 0
            ? { label: '$50(One-Time Credit) - M&H Discount', icon: activeEmailShell.installIcon, html: '$50(One-Time Credit) - M&amp;H Discount' }
            : null
    ].filter(Boolean);

    return {
        rows,
        warnings: [...new Set(internalWarnings)],
        homeSubtotal,
        regularTotal,
        totalSavings,
        hasHomeServices,
        billCredit,
        currentMonthlyBill,
        hasCurrentMonthlyBill,
        discountFootnote,
        isInternetOnlyEmail: Boolean(selectedInternet && !selectedTv && !selectedPhone && rows.length === 1),
        customerIncludedFeatures
    };
}
